/**
 * useSearchQuery
 */

import {
    useState, useCallback, useMemo
} from 'react';

import {
    Queries,
    ApolloLib,
} from 'bdvp-graphql';
import {
    useMediaConfig, useLang, useDictionary
} from '../config';
import { useDidUpdate } from '../';

const { useQuery } = ApolloLib;
const { SEARCH_WITH_PLAYBACK } = Queries;

const useSearchQueryPlayback = (searchQuery, searchOptions = {}) => {

    const {
        ssr = false,
        skip = !searchQuery,
        limit = 36,
        contentPreference,
        searchPage = 0,
        queryType,
    } = searchOptions;

    const lang = useLang();
    const dictionary = useDictionary();
    const { forgeInstance = 'MLB' } = useMediaConfig();

    const [
        page,
        setPage,
    ] = useState(searchPage);

    useDidUpdate(() => {

        setPage(searchPage);

    }, [searchQuery]);

    const [
        loadingMore,
        setLoadingMore,
    ] = useState(false);

    const {
        loading,
        data,
        error,
        fetchMore,
    } = useQuery(
        SEARCH_WITH_PLAYBACK,
        {
            variables: {
                query: searchQuery,
                limit,
                page: searchPage,
                languagePreference: lang.toUpperCase(),
                contentPreference,
                queryType,
                forgeInstance,
            },
            errorPolicy: 'all',
            ssr,
            skip,
        });

    const searchData = useMemo(() => {

        if (!data || !data.search) return null;

        const { search } = data;

        const entriesSoFar = (page + 1) * limit;
        const hasMore = search && search.total > 0 && entriesSoFar < search.total;

        return {
            topicPlayList: {
                hasMore: hasMore,
                mediaPlayback: search.plays.map(data => data.mediaPlayback[0]),
                title: dictionary.search_result ?? 'Search Results',
            },
        };

    }, [data]);

    const fetchMoreData = useCallback(() => {

        const nextPage = page + 1;

        setLoadingMore(true);

        return fetchMore({
            variables: {
                page: nextPage,
            },
            updateQuery: (prev, { fetchMoreResult }) => {

                setLoadingMore(false);
                setPage(nextPage);

                if (!fetchMoreResult) return prev;

                return Object.assign({}, prev, {
                    search: {
                        plays: [
                            ...prev.search.plays,
                            ...fetchMoreResult.search.plays,
                        ],
                        total: fetchMoreResult.search.total,
                        __typename: 'PagedClipDetails',
                    },
                });

            },
        });

    }, [
        fetchMore,
        page,
        setPage,
    ]);

    return {
        loading,
        loadingMore,
        data: searchData ? searchData : data,
        error,
        fetchMoreData,
        page,
        limit,
    };

};

export default useSearchQueryPlayback;
